<template>
    <div>
        <v-card v-bind="$attrs" class="elevation-2 my-3 mx-3 rounded-lg" @click="clicked">
            <v-card-text v-if="verifyingResults">
                <v-checkbox
                    :input-value="resultsToMarkOfficial.includes(result.id)"
                    hide-details
                    @change="$emit('changed-official', result, $event)"
                    @click.stop
                >
                    <template #label><strong>Official</strong></template>
                </v-checkbox>
            </v-card-text>
            <v-card-title class="py-1">
                <div v-if="result.event === 'TS'" class="d-flex flex-column mr-4">
                    <athlete-avatar :person="result.athlete" />
                    <athlete-avatar :person="result.athlete2" />
                </div>
                <v-avatar v-else size="60">
                    <athlete-avatar :person="result.athlete" />
                </v-avatar>

                <div>
                    <div class="mb-0 text-truncated" style="line-height: 1rem; max-width: 47vw">
                        <template v-if="result.event === 'TS'">
                            <div class="mb-1">
                                <inertia-link :href="`/athletes/${result.athlete.id}`">
                                    {{ result.athlete.name }}
                                </inertia-link>
                            </div>

                            <div>
                                <inertia-link :href="`/athletes/${result.athlete2.id}`">
                                    {{ result.athlete2.name }}
                                </inertia-link>
                            </div>
                        </template>
                        <template v-else>
                            <inertia-link :href="`/athletes/${result.athlete.id}`">
                                {{ result.athlete.name }}
                                <span v-if="result.athlete.is_birthday" role="img" aria-label="Birthday cake">
                                    🎂
                                </span>
                            </inertia-link>
                        </template>
                    </div>
                    <div class="grey--text caption mt-0">
                        <template v-if="result.event === 'TS'">
                            <div>
                                <country-flag
                                    v-if="meet.is_international && result.club.country"
                                    :country="result.club.country"
                                    width="20"
                                    class="elevation-1"
                                />
                                <inertia-link :href="`/clubs/${result.club.id}`">
                                    {{ result.club.name }}
                                </inertia-link>
                            </div>

                            <div v-if="!meet.is_international">
                                <inertia-link :href="`/clubs/${result.club2.id}`">
                                    {{ result.club2.name }}
                                </inertia-link>
                            </div>
                        </template>
                        <template v-else>
                            <country-flag
                                v-if="meet.is_international && result.club.country"
                                :country="result.club.country"
                                width="20"
                            />
                            <inertia-link :href="`/clubs/${result.club.id}`">
                                {{ result.club.name }}
                            </inertia-link>
                        </template>
                    </div>
                </div>

                <v-spacer />

                <div class="text-right">
                    <result-placement
                        :round="round"
                        :meet="meet"
                        :result="result"
                        :show-qual="result[`${round}_qual`]"
                        ordinal
                        @more-info="$emit('more-info')"
                    />

                    <br />

                    <template v-if="isAFinalRound && isCumulative">
                        <strong
                            :class="{
                                'yellow--text': result.changed(round, thresholdForChanges),
                                'text--darken-2': result.changed(round, thresholdForChanges) && $vuetify.theme.dark,
                                'text--darken-4': result.changed(round, thresholdForChanges) && !$vuetify.theme.dark,
                            }"
                        >
                            {{ numeral(result.total_score).format('0.000') }}
                        </strong>
                    </template>
                    <template v-else>
                        <strong
                            :class="{
                                'yellow--text': result.changed(round, thresholdForChanges),
                                'text--darken-2': result.changed(round, thresholdForChanges) && $vuetify.theme.dark,
                                'text--darken-4': result.changed(round, thresholdForChanges) && !$vuetify.theme.dark,
                            }"
                        >
                            {{ numeral(result[`${round}_score`]).format('0.000') }}
                        </strong>
                    </template>
                </div>
            </v-card-title>

            <v-card-text>
                <div v-if="showDivisionInfo">
                    <div class="flex font-weight-bold">
                        <span v-if="$vuetify.breakpoint.xsOnly">
                            {{ category.event_short_title }}
                        </span>
                        <span v-else>
                            {{ category.event_title }}
                        </span>
                        <span v-if="$vuetify.breakpoint.xsOnly"> &nbsp;- {{ category.level_title }} </span>
                        <span v-else-if="!meet.is_international"> &nbsp;- {{ category.level_title }} </span>

                        <span v-if="category.gender.toUpperCase() === 'M'" class="ml-2 mr-2">
                            <template v-if="meet.is_international">Men</template>
                            <template v-else>Male</template>
                        </span>
                        <span v-else-if="category.gender.toUpperCase() === 'F'" class="ml-2 mr-2">
                            <template v-if="meet.is_international">Women</template>
                            <template v-else>Female</template>
                        </span>
                        <span v-else-if="category.gender.toUpperCase() === 'C'" class="ml-2 mr-2"> Mixed </span>
                        <span v-if="!category.is_elite">
                            {{ category.age_group }}
                        </span>
                    </div>
                </div>
                <v-simple-table dense class="score-table">
                    <thead>
                        <tr v-if="resultCategory.event === 'AA'">
                            <th>Trampoline</th>
                            <th>Double-Mini</th>
                            <th>Tumbling</th>
                        </tr>
                        <tr v-else>
                            <th>#</th>
                            <th>Ex</th>
                            <th v-if="result.event === 'TI' || result.event === 'TS'">HD</th>
                            <th v-if="result.event === 'TS'">SY</th>
                            <th v-if="result.event === 'TI'">ToF</th>
                            <th>DD</th>
                            <th>ND</th>
                            <th>Total</th>
                            <th v-if="!hideVideo">Video</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="resultCategory.event === 'AA'">
                            <tr>
                                <td>{{ numeral(result.tra_result?.prelims_score).format('0.000') }}</td>
                                <td>{{ numeral(result.dmt_result?.prelims_score).format('0.000') }}</td>
                                <td>{{ numeral(result.tum_result?.prelims_score).format('0.000') }}</td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr v-for="routine in result.routineResultsForRound(round)" :key="routine.id">
                                <td>
                                    {{ routine.num }}
                                </td>
                                <td>
                                    <score-component
                                        :routine="routine"
                                        :threshold="thresholdForChanges"
                                        component="execution"
                                    />
                                </td>
                                <td v-if="result.event === 'TI' || result.event === 'TS'">
                                    <score-component
                                        :routine="routine"
                                        :threshold="thresholdForChanges"
                                        component="hd"
                                    />
                                </td>
                                <td v-if="result.event === 'TS'">
                                    <score-component
                                        :routine="routine"
                                        :threshold="thresholdForChanges"
                                        component="sync"
                                    />
                                </td>
                                <td v-if="result.event === 'TI'">
                                    <score-component
                                        :routine="routine"
                                        :threshold="thresholdForChanges"
                                        component="tof"
                                    />
                                </td>
                                <td>
                                    <score-component
                                        :routine="routine"
                                        :threshold="thresholdForChanges"
                                        component="dd"
                                    />
                                </td>
                                <td>
                                    <score-component
                                        :routine="routine"
                                        :threshold="thresholdForChanges"
                                        component="nd"
                                    />
                                </td>
                                <td>
                                    <score-component
                                        :routine="routine"
                                        :threshold="thresholdForChanges"
                                        component="total"
                                    />
                                </td>
                                <td v-if="!hideVideo">
                                    <scorsync-video
                                        :key="`video-${routine.id}`"
                                        :routine="routine"
                                        :result="result"
                                        :result-category="resultCategory || result.category"
                                        :round="round"
                                        size="24"
                                        @opened="$emit('video-opened')"
                                        @closed="$emit('video-closed')"
                                    />
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </v-simple-table>

                <div class="d-flex flex-row justify-space-between mt-2">
                    <!-- Official status -->
                    <div v-if="result[`${round}_marked_official_at`]">
                        <div class="text-caption font-weight-bold text-uppercase"></div>
                        <v-tooltip bottom @click.stop>
                            <template #activator="{ on, attrs }">
                                <v-icon
                                    v-if="result[`${round}_marked_official_at`]"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click.stop
                                >
                                    mdi-check-decagram
                                </v-icon>
                                <v-icon v-else color="grey lighten-2" v-bind="attrs" v-on="on" @click.stop>
                                    mdi-close
                                </v-icon>
                            </template>

                            <span v-if="result[`${round}_marked_official_at`]">Official</span>
                            <span v-else>Unfficial</span>
                        </v-tooltip>
                    </div>

                    <!-- Qualification status -->
                    <div v-if="canQualify">
                        <div class="text-caption font-weight-bold text-uppercase">Qual</div>
                        <v-icon v-if="result.qualified" color="green">check</v-icon>
                        <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
                    </div>

                    <!-- Mobility status -->
                    <div v-if="canMobilize" class="text-center">
                        <div class="text-caption font-weight-bold text-uppercase">Mob</div>
                        <mobility-check
                            :result="result"
                            :mobility="mobilityFor(result)"
                            :meet="meet"
                            bg-failed="white"
                            fg-failed="black"
                        />
                    </div>

                    <!-- World Championships Preferential -->
                    <div v-if="teamQualifiers.length > 0 && hasQualifier('world-championships')" class="text-center">
                        <div class="text-caption font-weight-bold text-uppercase">WC P</div>
                        <v-icon v-if="selectionScoreHit(result, 'world-championships', 'preferential')" color="green">
                            check
                        </v-icon>
                        <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
                    </div>

                    <!-- World Championships Minimum -->
                    <div v-if="teamQualifiers.length > 0 && hasQualifier('world-championships')" class="text-center">
                        <div class="text-caption font-weight-bold text-uppercase">WC M</div>
                        <v-icon v-if="selectionScoreHit(result, 'world-championships', 'minimum')" color="green">
                            check
                        </v-icon>
                        <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
                    </div>

                    <!-- World Age Group Championships Preferential -->
                    <div
                        v-if="teamQualifiers.length > 0 && hasQualifier('world-age-group-championships')"
                        class="text-center"
                    >
                        <div class="text-caption font-weight-bold text-uppercase">WAGC P</div>
                        <v-icon
                            v-if="selectionScoreHit(result, 'world-age-group-championships', 'preferential')"
                            color="green"
                        >
                            check
                        </v-icon>
                        <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
                    </div>

                    <!-- World Age Group Championships Minimum -->
                    <div
                        v-if="teamQualifiers.length > 0 && hasQualifier('world-age-group-championships')"
                        class="text-center"
                    >
                        <div class="text-caption font-weight-bold text-uppercase">WAGC M</div>
                        <v-icon
                            v-if="selectionScoreHit(result, 'world-age-group-championships', 'minimum')"
                            color="green"
                        >
                            check
                        </v-icon>
                        <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
                    </div>

                    <!-- Senior National Team -->
                    <div v-if="teamQualifiers.length > 0 && hasQualifier('senior-national-team')" class="text-center">
                        <div class="text-caption font-weight-bold text-uppercase">SR NT</div>
                        <v-icon v-if="selectionScoreHit(result, 'senior-national-team', 'minimum')" color="green">
                            check
                        </v-icon>
                        <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
                    </div>

                    <!-- Junior National Team -->
                    <div v-if="teamQualifiers.length > 0 && hasQualifier('junior-national-team')" class="text-center">
                        <div class="text-caption font-weight-bold text-uppercase">JR NT</div>
                        <v-icon v-if="selectionScoreHit(result, 'junior-national-team', 'minimum')" color="green">
                            check
                        </v-icon>
                        <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
                    </div>
                </div>

                <!-- Share button -->
                <scorsync-share v-if="false" :result="result" :round="round" />
            </v-card-text>
        </v-card>

        <!-- Dialog for changes -->
        <v-dialog v-model="dialog" max-width="600">
            <v-card>
                <v-card-title class="headline">Previous Scores</v-card-title>
                <v-card-text>
                    <v-list>
                        <v-list-item-group>
                            <v-list-item
                                v-for="routine in result
                                    .routineResultsForRound(round)
                                    .filter((routine) => routine.score_changed)"
                                :key="routine.id"
                            >
                                <v-list-item-content>
                                    <v-list-item-title> Routine {{ routine.num }} </v-list-item-title>
                                    <v-list-item-subtitle>
                                        <div v-for="change in routine.changedFields(thresholdForChanges)" :key="change">
                                            {{ change.toUpperCase() }}:
                                            <span class="font-weight-bold">
                                                {{ routine.changed(thresholdForChanges).old[change] }}
                                            </span>
                                        </div>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" text @click="dialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import numeral from 'numeral';
import ScorsyncVideo from '@/Components/ScorsyncVideo.vue';
import AthleteAvatar from '@/Components/ScorsyncAvatar.vue';
import CountryFlag from '@/Components/CountryFlag.vue';
import ResultPlacement from '@/Components/Results/ResultPlacement.vue';
import ScorsyncShare from '@/Components/ScorsyncShare.vue';
import Result from '@/Models/Result.js';
import ResultCategory from '@/Models/ResultCategory.js';
import Meet from '@/Models/Meet.js';
import MobilityCheck from '@/Components/MobilityCheck.vue';
import ScoreComponent from '@/Components/Results/ScoreComponent.vue';

export default {
    name: 'ResultCard',

    components: {
        ScoreComponent,
        ScorsyncShare,
        MobilityCheck,
        ResultPlacement,
        CountryFlag,
        AthleteAvatar,
        ScorsyncVideo,
    },

    props: {
        result: {
            type: Result,
            required: true,
        },

        round: {
            type: String,
            required: true,
        },

        meet: {
            type: Meet,
            required: true,
        },

        resultCategory: {
            type: ResultCategory,
            required: false,
            default: null,
        },

        canMobilize: {
            type: Boolean,
            default: false,
        },

        canQualify: {
            type: Boolean,
            default: false,
        },

        isAFinalRound: {
            type: Boolean,
            default: false,
        },

        thresholdForChanges: {
            type: Number,
            default: 60,
        },

        isCumulative: {
            type: Boolean,
            default: false,
        },

        teamQualifiers: {
            type: Array,
            default: () => [],
        },

        mobilities: {
            type: Array,
            default: () => [],
        },

        showDivisionInfo: {
            type: Boolean,
            default: false,
        },

        hideVideo: {
            type: Boolean,
            default: false,
        },

        verifyingResults: {
            type: Boolean,
            default: false,
        },

        resultsToMarkOfficial: {
            type: Array,
            default: () => [],
        },
    },

    emits: ['click', 'more-info', 'video-opened', 'video-closed', 'changed-official'],

    data() {
        return {
            dialog: false,
        };
    },

    computed: {
        category() {
            return this.resultCategory || this.result.category;
        },

        teamQualifierKeys() {
            return this.teamQualifiers.map((teamQualifier) => teamQualifier.key);
        },
    },

    methods: {
        numeral,

        selectionScoreHit(result, teamQualifierKey, scoreKey) {
            const qualifier = result.selection_score_report.find((selectionScoreReport) =>
                selectionScoreReport.teamQualifier.key.endsWith(teamQualifierKey),
            );

            return qualifier && qualifier.report[`${scoreKey}ScoreHit`];
        },

        mobilityFor(result) {
            // Adjust this because mobility.min_age and mobility.max_age can be null
            return this.mobilities.filter((mobility) => {
                if (mobility.min_age === null && mobility.max_age === null) return true;
                if (mobility.min_age === null) return result.athlete.age <= mobility.max_age;
                if (mobility.max_age === null) return result.athlete.age >= mobility.min_age;
                return result.athlete.age >= mobility.min_age && result.athlete.age <= mobility.max_age;
            });
        },

        clicked($event) {
            if (this.result.changed(this.round, this.thresholdForChanges)) {
                this.dialog = true;
            } else {
                this.$emit('click', $event);
            }
        },

        hasQualifier(key) {
            return (
                // Qualifier key could be "2024-junior-national-team" and we want to match "junior-national-team"
                this.teamQualifierKeys.filter((qualifierKey) => qualifierKey.includes(key)).length > 0
            );
        },
    },
};
</script>
